import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CUSTOM_INSTRUCTION_ERROR_MESSAGES } from '@constants/custom-instructions.constants';
import { ConfigService } from '@services/config.service';
import { CustomInstructionsService } from '@services/custom-instructions.service';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';

interface SaveAsForm {
  name: FormControl<string | null>;
}

export interface SaveAsDialogData {
  parentId: string;
  content: string;
  suggestedName?: string;
}

@Component({
  selector: 'app-save-as-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './save-as-dialog.component.html',
  styleUrls: ['./save-as-dialog.component.css'],
})
export class SaveAsDialogComponent implements OnInit {
  form!: FormGroup<SaveAsForm>;
  isLoading = signal(false);
  error = signal('');

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig<SaveAsDialogData>,
    private customInstructionsService: CustomInstructionsService,
    private destroyRef: DestroyRef,
    private configService: ConfigService,
  ) {}

  ngOnInit() {
    this.createForm();
  }

  private createForm() {
    const { data } = this.dialogConfig;
    const inputLengths = this.configService.inputLengths;

    this.form = this.formBuilder.group<SaveAsForm>({
      name: new FormControl(data?.suggestedName || '', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(inputLengths.CUSTOM_INSTRUCTION_NAME),
      ]),
    });

    // Listen for name changes to clear error (only when not loading)
    this.form.controls.name.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Only clear error if we're not in a loading state
        // This prevents clearing the error when the form is re-enabled after API call
        if (!this.isLoading()) {
          this.error.set('');
        }
      });
  }

  onSave() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const { data } = this.dialogConfig;
    const name = this.form.value.name?.trim() || '';

    if (!name) {
      this.error.set(CUSTOM_INSTRUCTION_ERROR_MESSAGES.NAME_REQUIRED);
      return;
    }

    this.isLoading.set(true);
    this.form.controls.name.disable();
    this.error.set('');

    // Check name availability first, then create branched instruction if available
    this.customInstructionsService
      .checkNameAvailability(name)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(nameCheck => {
          if (!nameCheck.available) {
            this.error.set(
              CUSTOM_INSTRUCTION_ERROR_MESSAGES.NAME_ALREADY_EXISTS,
            );
            throw new Error('Name not available');
          }

          // Create branched instruction if name is available
          return this.customInstructionsService.createBranchedInstruction(
            data.parentId,
            {
              name: name,
              content: data.content,
            },
          );
        }),
        catchError(error => {
          // Handle both name availability error and API errors
          if (error.message !== 'Name not available') {
            this.error.set(CUSTOM_INSTRUCTION_ERROR_MESSAGES.SAVE_FAILED);
          }
          return of(null); // Return null to indicate failure
        }),
        finalize(() => {
          // Re-enable form control first while loading is still true
          this.form.controls.name.enable();
          // Then set loading to false
          this.isLoading.set(false);
        }),
      )
      .subscribe(newInstruction => {
        if (newInstruction) {
          // Success - close dialog with the new instruction
          this.dialogRef.close(newInstruction);
        }
        // If newInstruction is null, it means there was an error
        // and the error message is already set in the catchError block
      });
  }

  onCancel() {
    this.dialogRef.close();
  }
}
